import * as React from 'react'
import { InsightWithSource } from '@shared/utils/insights/types'
import { ConversationData } from '@highlight/common'
import { usePositionedMouseListeners } from '@renderer/chat'
import { twMerge } from 'tailwind-merge'
import { motion } from 'framer-motion'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import TimeAgo from 'react-timeago'
import { useInsightProperties } from './hooks'
import { shortFormatter } from './utils'
import { InsightPopover } from './insight-popover'

export const InsightListItem = React.memo(
  (
    insight: InsightWithSource & {
      meetingNote?: ConversationData
      truncate?: boolean
      showPreview?: boolean
      showActions?: boolean
      onClick?: () => void
      index?: number
    },
  ) => {
    // Props
    const {
      id,
      screenSource,
      truncate,
      showActions,
      insight: insightText,
      meetingNote,
      showPreview,
      onClick,
      index = 0,
    } = insight

    // State
    const { icon, hasBackgroundImage, sourceLabel, onOpenInsight } = useInsightProperties(insight)
    const [imageError, setImageError] = React.useState(false)

    // Only update if the insight ID changes
    const insights = React.useMemo(() => {
      return [insight]
    }, [insight])

    const isResolved = React.useMemo(() => insight.status === 'completed', [insight.status])

    // Memoize click handlers
    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        if (typeof onClick === 'function') {
          onClick()
          return
        }
        e.stopPropagation()
        onOpenInsight()
        AmplitudeRenderer.trackEvent('Insights Action', {
          action: 'Open insight',
          type: meetingNote ? 'Meeting note' : 'Screen',
          source: 'List item',
        })
      },
      [onClick, onOpenInsight, meetingNote],
    )
    const clickListeners = usePositionedMouseListeners(handleClick, {
      releaseDelay: 100,
      ignoreClicksOnSelection: true,
    })

    // Entrance-only variants (decoupled from layout)
    const entrance = React.useMemo(
      () => ({
        hidden: { opacity: 0, y: 8, scale: 0.98 },
        show: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            type: 'spring',
            stiffness: 500,
            damping: 40,
            mass: 0.6,
            delay: index * 0.01, // ← stagger by index (optional)
          },
        },
        exit: { opacity: 0, y: -6 },
      }),
      [index],
    )

    return (
      <InsightPopover insights={insights} disabled={showActions === false} displayMode={'list'} offsetY={10}>
        {
          // @ts-ignore
          ({ isActive }) => {
            return (
              // OUTER: entrance animation only (no layout)
              <motion.div key={`entrance-${id}`} initial="hidden" animate="show" exit="exit" variants={entrance}>
                {/* INNER: layout reflow animation only, gated off on first paint */}
                <motion.div
                  key={id}
                  layout={false}
                  layoutId={id}
                  initial={false} // don't run "initial" for layout FLIP
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 40,
                    mass: 0.6,
                    layout: { type: 'spring', stiffness: 600, damping: 40 },
                  }}
                  className={twMerge(
                    'group',
                    'w-full',
                    'flex-shrink-0',
                    'relative flex cursor-pointer rounded-xl',
                    'border-light/5 hover:border-light/20 border',
                    isActive && 'border-light/20',
                    'shadow-md transition group-hover:shadow-lg',
                    !isResolved && 'from-light/5 bg-gradient-to-br to-black/10 to-90%',
                    isResolved ? 'bg-secondary-old' : 'bg-tertiary',
                  )}
                  {...clickListeners}
                >
                  <div
                    className={'relative aspect-square h-full min-h-[64px] min-w-[64px]'}
                    style={{
                      maskImage: 'linear-gradient(to right, black 60%, transparent 100%)',
                    }}
                  >
                    {hasBackgroundImage && !imageError && (
                      <img
                        src={
                          screenSource?.screenshotUuid
                            ? `screenshot://${screenSource.screenshotUuid}?h=128`
                            : screenSource?.base64DataUrl
                        }
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className={twMerge(
                          'absolute left-0 top-0 h-full w-full rounded-l-xl object-cover blur-[3px] transition group-hover:opacity-100 group-hover:blur-none',
                          isResolved ? 'opacity-25' : 'opacity-50',
                        )}
                        onLoad={() => setImageError(false)}
                        onError={() => setImageError(true)}
                      />
                    )}

                    {(!hasBackgroundImage || imageError) && icon && showPreview && !isResolved && (
                      <div className="absolute overflow-hidden rounded-xl">
                        <div
                          className={'flex h-full w-full -translate-x-4 -translate-y-4 scale-125 opacity-15 blur-md'}
                        >
                          {React.cloneElement(icon, {
                            className: 'h-16 w-16',
                            size: 64,
                          })}
                        </div>
                      </div>
                    )}

                    {icon && (
                      <div
                        className={twMerge(
                          'absolute left-2 top-2 z-[2] transition-opacity group-hover:opacity-100',
                          isResolved ? 'opacity-30' : 'opacity-60',
                        )}
                      >
                        {icon}
                      </div>
                    )}
                  </div>

                  <div className={'flex w-full min-w-0 flex-col'}>
                    <div className={'flex flex-col gap-0.5 p-3'}>
                      <span
                        className={twMerge(
                          'truncate text-sm font-normal',
                          isResolved ? 'text-light/20' : 'text-light/40',
                        )}
                      >
                        {insight.environmentName && insight.environmentName !== 'Default' && (
                          <span
                            className={
                              'text-yellow-subtle/70 bg-yellow/5 me-2 h-fit w-fit rounded-md px-1 py-0.5 text-xs font-normal'
                            }
                          >
                            {insight.environmentName}
                          </span>
                        )}
                        <TimeAgo
                          date={insight.createdAt}
                          formatter={shortFormatter}
                          minPeriod={60} // don't update more than once per minute
                          live
                        />
                        {' • '}
                        {meetingNote?.title ??
                          meetingNote?.meeting?.windowTitle ??
                          meetingNote?.meeting?.app?.name ??
                          sourceLabel}
                      </span>
                      <span
                        className={twMerge(
                          'font-350 cursor-text select-text text-sm leading-normal',
                          truncate && 'line-clamp-1',
                          isResolved ? 'text-light/20 line-through' : 'text-light/80',
                        )}
                      >
                        {insightText}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )
          }
        }
      </InsightPopover>
    )
  },
)
InsightListItem.displayName = 'InsightListItem'
